import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { ProcessingStage, VoiceInteractionResult, WebSocketContextType } from '../models';
import { WebSocketService, VoiceService } from '../services';

// Re-export types for backward compatibility
export type { ProcessingStage, VoiceInteractionResult } from '../models';

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

const wsUrl = process.env.REACT_APP_WEBSOCKET_URL;

if (!wsUrl) {
  throw new Error('REACT_APP_WEBSOCKET_URL is not set. Please set it in your environment.');
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  // Initialize services
  const webSocketServiceRef = React.useRef<WebSocketService>(new WebSocketService(wsUrl));
  const voiceServiceRef = React.useRef<VoiceService>(new VoiceService(webSocketServiceRef.current));
  
  // State management
  const [isConnected, setIsConnected] = useState(webSocketServiceRef.current.isConnected);
  const [isConnecting, setIsConnecting] = useState(webSocketServiceRef.current.isConnecting);
  const [reconnectAttempts, setReconnectAttempts] = useState(webSocketServiceRef.current.getReconnectAttempts());
  
  // Setup connection state change listener
  useEffect(() => {
    const removeListener = webSocketServiceRef.current.addConnectionListener((connected) => {
      setIsConnected(webSocketServiceRef.current.isConnected);
      setIsConnecting(webSocketServiceRef.current.isConnecting);
      setReconnectAttempts(webSocketServiceRef.current.getReconnectAttempts());
    });
    
    // Auto-connect on mount
    if (!webSocketServiceRef.current.isConnected && !webSocketServiceRef.current.isConnecting) {
      webSocketServiceRef.current.connect().catch((error) => {
        console.error('Initial connection failed:', error);
      });
    }

    return () => {
      removeListener();
      // Don't cleanup on unmount in development due to StrictMode
      if (process.env.NODE_ENV !== 'development') {
        webSocketServiceRef.current.disconnect();
      }
    };
  }, []);

  const connect = useCallback(async (): Promise<void> => {
    return webSocketServiceRef.current.connect();
  }, []);

  const disconnect = useCallback(() => {
    webSocketServiceRef.current.disconnect();
  }, []);

  const processVoiceInteraction = useCallback(async (
    audioData: string,
    audioFormat: string,
    conversationId?: string,
    onProgress?: (stage: ProcessingStage) => void
  ): Promise<VoiceInteractionResult> => {
    return voiceServiceRef.current.processVoiceInteraction(
      audioData,
      audioFormat,
      conversationId,
      onProgress
    );
  }, []);

  const contextValue: WebSocketContextType = {
    isConnected,
    isConnecting,
    reconnectAttempts,
    connect,
    disconnect,
    processVoiceInteraction
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}; 